import React, { useEffect, useRef, useState } from "react";

import Map from "@arcgis/core/Map";
import MapView from "@arcgis/core/views/MapView";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Graphic from "@arcgis/core/Graphic";

import "@arcgis/core/assets/esri/themes/light/main.css";
import { useNavigate } from "react-router-dom";

// ======================================================
// API
// ======================================================

const API_URL =
  "https://urbangis.mp.gov.in/api/Station_Data/GetScadaWTPDataNEW";


// ======================================================
// LOCATION PIN SVG
// ======================================================

const createPinSVG = (color) => {

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg"
         width="48"
         height="58"
         viewBox="0 0 48 58">

      <defs>
        <filter id="shadow">
          <feDropShadow
            dx="0"
            dy="2"
            stdDeviation="2"
            flood-opacity="0.35"
          />
        </filter>
      </defs>

      <path
        d="
          M24 2
          C11.8 2 2 11.5 2 23.2
          C2 38.5 24 56 24 56
          C24 56 46 38.5 46 23.2
          C46 11.5 36.2 2 24 2
          Z
        "
        fill="${color}"
        stroke="#ffffff"
        stroke-width="3"
        filter="url(#shadow)"
      />

      <circle
        cx="24"
        cy="23"
        r="8"
        fill="#ffffff"
      />

    </svg>
  `;

  return (
    "data:image/svg+xml;charset=UTF-8," +
    encodeURIComponent(svg)
  );
};


// ======================================================
// COLORS
// ======================================================

const COLORS = {
  intake: "#2563eb",
  wtp: "#16a34a",
  oht: "#f97316",
};


// ======================================================
// CREATE PIN SYMBOL
// ======================================================

const createPinSymbol = (type) => {

  return {
    type: "picture-marker",

    url: createPinSVG(
      COLORS[type]
    ),

    width: "32px",
    height: "40px",

    // Bottom center of the pin
    xoffset: "0px",
    yoffset: "18px",
  };
};


// ======================================================
// FLATTEN API DATA
// ======================================================

const convertApiDataToMarkers = (apiData) => {

  const result = [];

  apiData.forEach((item) => {

    // ==================================================
    // INTAKE
    // ==================================================

    const intake = item.intake;

    if (intake) {

      const lat = Number(intake.intake_lat);
      const lng = Number(intake.intake_long);

      if (
        Number.isFinite(lat) &&
        Number.isFinite(lng) &&
        lat !== 0 &&
        lng !== 0
      ) {

        result.push({

          type: "intake",

          id: intake.intakeId,

          name: intake.intakeWellName,

          lat,
          lng,

          district: intake.districtName,
          districtCode: intake.district_Code,

          ulb: intake.ulbName,
          ulbCode: intake.ulb_Code,

          scheme: intake.schemeName,

          level: intake.intakeWellLevel,

          outletFlow: intake.outletFlow,

          pressure: intake.headerActualPressure,

          originalData: intake,
        });
      }
    }


    // ==================================================
    // WTP
    // ==================================================

    (item.wtpUnits || []).forEach((unit) => {

      const wtp = unit.wtp;

      if (wtp) {

        const lat = Number(wtp.wtp_lat);
        const lng = Number(wtp.wtp_long);

        if (
          Number.isFinite(lat) &&
          Number.isFinite(lng) &&
          lat !== 0 &&
          lng !== 0
        ) {

          result.push({

            type: "wtp",

            id: wtp.wtP_Device_id,

            name: wtp.wtP_Name,

            lat,
            lng,

            district: wtp.districtName,
            districtCode: wtp.district_Code,

            ulb: wtp.ulbName,
            ulbCode: wtp.ulb_Code,

            inletFlow: wtp.inletFlow_mld,
            outletFlow: wtp.outletFlow_mld,

            rawPh: wtp.rawPh,
            treatedPh: wtp.treatedPh,

            chlorine: wtp.chlorine,

            turbidity: wtp.treatedTurbidity,

            originalData: wtp,
          });
        }
      }


      // ==================================================
      // OHT
      // ==================================================

      (unit.ohts || []).forEach((oht) => {

        const lat = Number(oht.oht_lat);
        const lng = Number(oht.oht_long);

        if (
          Number.isFinite(lat) &&
          Number.isFinite(lng) &&
          lat !== 0 &&
          lng !== 0
        ) {

          result.push({

            type: "oht",

            id: oht.ohT_Device_id,

            name: oht.ohT_Name,

            lat,
            lng,

            district: oht.districtName,
            districtCode: oht.district_Code,

            ulb: oht.ulbName,
            ulbCode: oht.ulb_Code,

            waterLevel: oht.waterLevel_mld,

            inletFlow: oht.inletFlow_mld,

            chlorine: oht.residualChlorine,

            pressure: oht.inletPressure,

            originalData: oht,
          });
        }

      });

    });

  });

  return result;
};


// ======================================================
// MAIN COMPONENT
// ======================================================

export default function SCADAWTPMap() {
 const navigate = useNavigate();
  const mapDiv = useRef(null);

  const viewRef = useRef(null);

  const layerRef = useRef(null);


  const [markers, setMarkers] = useState([]);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");


  // ====================================================
  // COUNTS
  // ====================================================

  const intakeCount =
    markers.filter(
      (x) => x.type === "intake"
    ).length;

  const wtpCount =
    markers.filter(
      (x) => x.type === "wtp"
    ).length;

  const ohtCount =
    markers.filter(
      (x) => x.type === "oht"
    ).length;


  // ====================================================
  // LOAD MAP
  // ====================================================

  useEffect(() => {

    if (!mapDiv.current) {
      return;
    }


    const graphicsLayer =
      new GraphicsLayer({
        title: "SCADA Water Infrastructure",
      });


    layerRef.current =
      graphicsLayer;


    const map = new Map({

      basemap: "streets-navigation-vector",

      layers: [
        graphicsLayer
      ],

    });


    const view = new MapView({

      container: mapDiv.current,

      map,

      center: [
        77.4,
        23.25
      ],

      zoom: 6,

      constraints: {
        minZoom: 5,
        maxZoom: 18,
      },

    });


    viewRef.current = view;


    return () => {

      if (viewRef.current) {

        viewRef.current.destroy();

        viewRef.current = null;
      }

      layerRef.current = null;

    };

  }, []);


  // ====================================================
  // FETCH API
  // ====================================================

  useEffect(() => {

    const loadData = async () => {

      try {

        setLoading(true);

        setError("");


        const response = await fetch(
          API_URL,
          {
            method: "POST",

            headers: {
              "Content-Type":
                "application/json",

              Accept:
                "application/json",
            },

            body: JSON.stringify({

              intakeID: null,

              distCd: null,

              ulbCd: null,

            }),

          }
        );


        if (!response.ok) {

          throw new Error(
            `HTTP Error ${response.status}`
          );

        }


        const result =
          await response.json();


        console.log(
          "SCADA API RESPONSE:",
          result
        );


        if (
          result.statusCode !== 200
        ) {

          throw new Error(
            result.message ||
            "API returned an error"
          );

        }


        const flattened =
          convertApiDataToMarkers(
            result.data || []
          );


        console.log(
          "MAP MARKERS:",
          flattened
        );


        setMarkers(flattened);

      }
      catch (err) {

        console.error(
          "SCADA API ERROR:",
          err
        );

        setError(
          err.message ||
          "Unable to load SCADA data"
        );

      }
      finally {

        setLoading(false);

      }

    };


    loadData();

  }, []);


  // ====================================================
  // ADD MARKERS TO ARCGIS
  // ====================================================

  useEffect(() => {

    const layer =
      layerRef.current;

    const view =
      viewRef.current;


    if (!layer || !view) {
      return;
    }


    layer.removeAll();


    if (!markers.length) {
      return;
    }


    const graphics =
      markers.map((marker) => {

        const graphic =
          new Graphic({

            geometry: {

              type: "point",

              longitude:
                marker.lng,

              latitude:
                marker.lat,

            },


            symbol:
              createPinSymbol(
                marker.type
              ),


            attributes: {

              ...marker,

              scadaType:
                marker.type,

            },


            popupTemplate: {

              title:
                `<b>${marker.type.toUpperCase()}</b><br/>${marker.name}`,

              content: [

                {

                  type: "fields",

                  fieldInfos: [

                    {
                      fieldName:
                        "id",

                      label:
                        "Device ID",

                    },

                    {
                      fieldName:
                        "district",

                      label:
                        "District",

                    },

                    {
                      fieldName:
                        "ulb",

                      label:
                        "ULB",

                    },

                    {
                      fieldName:
                        "lat",

                      label:
                        "Latitude",

                      format: {
                        places: 6,
                      },

                    },

                    {
                      fieldName:
                        "lng",

                      label:
                        "Longitude",

                      format: {
                        places: 6,
                      },

                    },

                    {
                      fieldName:
                        "scheme",

                      label:
                        "Scheme",

                    },

                    {
                      fieldName:
                        "level",

                      label:
                        "Intake Level",

                    },

                    {
                      fieldName:
                        "outletFlow",

                      label:
                        "Outlet Flow",

                    },

                    {
                      fieldName:
                        "inletFlow",

                      label:
                        "Inlet Flow",

                    },

                    {
                      fieldName:
                        "outletFlow",

                      label:
                        "Outlet Flow",

                    },

                    {
                      fieldName:
                        "rawPh",

                      label:
                        "Raw pH",

                    },

                    {
                      fieldName:
                        "treatedPh",

                      label:
                        "Treated pH",

                    },

                    {
                      fieldName:
                        "chlorine",

                      label:
                        "Chlorine",

                    },

                    {
                      fieldName:
                        "turbidity",

                      label:
                        "Turbidity",

                    },

                    {
                      fieldName:
                        "waterLevel",

                      label:
                        "Water Level",

                    },

                    {
                      fieldName:
                        "pressure",

                      label:
                        "Pressure",

                    },

                  ],

                },

              ],

            },

          });


        return graphic;

      });


    layer.addMany(graphics);


    // ==================================================
    // ZOOM TO MARKERS
    // ==================================================

    view.goTo(
      graphics.map(
        (g) => g.geometry
      ),
      {
        duration: 1000,
        padding: 80,
      }
    );

  }, [markers]);


  // ====================================================
  // UI
  // ====================================================

  return (

    <div
      style={{
        width: "100%",
        height: "100vh",
        position: "relative",
        overflow: "hidden",
      }}
    >


      {/* ============================================== */}
      {/* HEADER */}
      {/* ============================================== */}

      <div
        style={{
          position: "absolute",

          top: 15,
          left: 15,

          zIndex: 100,

          background: "#ffffff",

          padding:
            "12px 18px",

          borderRadius: 10,

          boxShadow:
            "0 3px 15px rgba(0,0,0,.25)",

          minWidth: 330,
        }}
      >

        <div
          style={{
            fontSize: 18,
            fontWeight: 700,
            color: "#1f2937",
          }}
        >
          MP Water Supply SCADA
        </div>


        <div
          style={{
            fontSize: 12,
            color: "#6b7280",
            marginTop: 3,
          }}
        >
          Intake • WTP • OHT Monitoring
        </div>


        {/* ========================================== */}
        {/* COUNTS */}
        {/* ========================================== */}
<button
  onClick={() => navigate("/SCADAWTPDashboard")}
  style={{
    height: "38px",
    border: "none",
    borderRadius: "6px",
    padding: "0 14px",
    color: "#fff",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: "600",
    whiteSpace: "nowrap",
    background: "linear-gradient(135deg,#24324f,#34476c)",
    boxShadow: "0 2px 5px rgba(0,0,0,0.15)",
  }}
>
  ← Back
</button>
        <div
          style={{
            display: "flex",
            gap: 8,
            marginTop: 12,
          }}
        >

          <CountBox
            color="#2563eb"
            icon="📍"
            title="Intake"
            count={intakeCount}
          />


          <CountBox
            color="#16a34a"
            icon="📍"
            title="WTP"
            count={wtpCount}
          />


          <CountBox
            color="#f97316"
            icon="📍"
            title="OHT"
            count={ohtCount}
          />

        </div>


        {/* ========================================== */}
        {/* LOADING */}
        {/* ========================================== */}

        {loading && (

          <div
            style={{
              marginTop: 10,
              color: "#2563eb",
              fontSize: 12,
            }}
          >
            Loading SCADA data...
          </div>

        )}


        {/* ========================================== */}
        {/* ERROR */}
        {/* ========================================== */}

        {error && (

          <div
            style={{
              marginTop: 10,
              color: "#dc2626",
              fontSize: 12,
            }}
          >
            {error}
          </div>

        )}

      </div>


      {/* ============================================== */}
      {/* MAP */}
      {/* ============================================== */}

      <div
        ref={mapDiv}
        style={{
          width: "100%",
          height: "100%",
        }}
      />

    </div>

  );
}


// ======================================================
// COUNT BOX
// ======================================================

function CountBox({
  color,
  icon,
  title,
  count,
}) {

  return (

    <div
      style={{
        flex: 1,

        background:
          color,

        color: "#ffffff",

        borderRadius: 7,

        padding:
          "8px 10px",

        textAlign:
          "center",

        minWidth: 70,
      }}
    >

      <div
        style={{
          fontSize: 16,
        }}
      >
        {icon}
      </div>


      <div
        style={{
          fontSize: 20,
          fontWeight: 700,
        }}
      >
        {count}
      </div>


      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
        }}
      >
        {title}
      </div>

    </div>

  );

}
